import axios from './axios';

/**
 * SnapTrade API Service
 * Handles all SnapTrade-related API calls
 */

const SNAPTRADE_BASE = '/snaptrade';

/**
 * Register user with SnapTrade (idempotent)
 * @param {string} userId - MongoDB user ID
 */
export const registerUser = (userId) => axios.post(`${SNAPTRADE_BASE}/register-user`, { userId });

/**
 * Get SnapTrade connection URL for brokerage portal
 * @param {string} userId - MongoDB user ID
 * @param {string} redirectUrl - Optional redirect URL after connection
 */
export const getConnectionUrl = (userId, redirectUrl = null) => {
    const params = new URLSearchParams({ userId });
    if (redirectUrl) params.append('redirectUrl', redirectUrl);
    return axios.get(`${SNAPTRADE_BASE}/connection-url?${params.toString()}`);
};

/**
 * List all brokerage connections for a user
 * @param {string} userId - MongoDB user ID
 */
export const listConnections = (userId) => {
    const params = new URLSearchParams({ userId });
    return axios.get(`${SNAPTRADE_BASE}/connections?${params.toString()}`);
};

/**
 * List all brokerage accounts for a user
 * @param {string} userId - MongoDB user ID
 */
export const listAccounts = (userId) => {
    const params = new URLSearchParams({ userId });
    return axios.get(`${SNAPTRADE_BASE}/accounts?${params.toString()}`);
};

/**
 * Preview activities/trades without saving (dry run)
 * @param {Object} params
 * @param {string} params.userId - MongoDB user ID
 * @param {string} params.snaptradeAccountId - SnapTrade account UUID
 * @param {string} params.journalAccountId - Journal account MongoDB ID
 * @param {string} params.startDate - ISO date string (YYYY-MM-DD)
 * @param {string} params.endDate - ISO date string (YYYY-MM-DD)
 */
export const previewActivities = ({
    userId,
    snaptradeAccountId,
    journalAccountId,
    startDate,
    endDate
}) =>
    axios.post(`${SNAPTRADE_BASE}/sync-activities`, {
        userId,
        snaptradeAccountId,
        journalAccountId,
        startDate,
        endDate
    });

/**
 * Import trades to journal database
 * @param {Object} params
 * @param {string} params.userId - MongoDB user ID
 * @param {string} params.snaptradeAccountId - SnapTrade account UUID
 * @param {string} params.journalAccountId - Journal account MongoDB ID
 * @param {string} params.startDate - ISO date string (YYYY-MM-DD)
 * @param {string} params.endDate - ISO date string (YYYY-MM-DD)
 */
export const importTrades = ({
    userId,
    snaptradeAccountId,
    journalAccountId,
    startDate,
    endDate
}) =>
    axios.post(`${SNAPTRADE_BASE}/import-trades`, {
        userId,
        snaptradeAccountId,
        journalAccountId,
        startDate,
        endDate
    });

/**
 * Refresh brokerage connection data
 * @param {string} userId - MongoDB user ID
 * @param {string} authorizationId - Brokerage authorization ID
 */
export const refreshConnection = (userId, authorizationId) =>
    axios.post(`${SNAPTRADE_BASE}/refresh-connection`, {
        userId,
        authorizationId
    });

/**
 * Get current positions for an account
 * @param {string} userId - MongoDB user ID
 * @param {string} accountId - SnapTrade account UUID
 */
export const getPositions = (userId, accountId) => {
    const params = new URLSearchParams({ userId, accountId });
    return axios.get(`${SNAPTRADE_BASE}/positions?${params.toString()}`);
};
